import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Lock,
  User,
  ArrowRight,
  Chrome,
  AlertCircle,
  X,
  Sparkles,
  Users,
  Trophy,
  Flame,
  Check,
  Camera,
  Upload,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/common/Button";
import { Input } from "../components/common/Input";
import { UsernameSetupModal } from "../components/common/UsernameSetupModal";
import Logo from "../components/common/Logo";

// Default avatar path
const DEFAULT_AVATAR = "/assets/avatar.svg";

type AuthMode = "login" | "signup" | "forgot";

// Pre-Register Intro Modal
const IntroModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  gtBonus: number;
  hasTag: boolean;
}> = ({ isOpen, onClose, gtBonus, hasTag }) => {
  const features = [
    {
      icon: Flame,
      title: "SweatRooms",
      desc: "Live reactions with the community",
    },
    {
      icon: Sparkles,
      title: "GroundTokens",
      desc: "Earn & spend GT in the ecosystem",
    },
    {
      icon: Trophy,
      title: "Daily Games",
      desc: "Trivia, streaks & competitions",
    },
    {
      icon: Users,
      title: "Community",
      desc: "Connect with sports fans worldwide",
    },
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-md max-h-[90vh] bg-[#0A0A0A] border border-primary/30 rounded-2xl overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="sticky top-4 float-right mr-4 mt-4 p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors z-10"
          >
            <X size={20} />
          </button>

          {/* Header */}
          <div className="px-8 pb-0 text-center -mt-8">
            {/* Early Access Badge */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 border border-primary/30 rounded-full mb-4"
            >
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <span className="text-primary text-xs font-semibold uppercase tracking-wider">
                Early Access
              </span>
            </motion.div>

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="inline-flex items-center gap-2 mb-6"
            >
              <Logo size="md" variant="icon-only" forceTheme="dark" />{" "}
              <span className="text-2xl font-bold text-primary">PARLAY</span>
              <span className="text-2xl font-bold text-white">GROUND</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-2xl font-bold text-white mb-2"
            >
              You're Early - Get In Now
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-text-body"
            >
              Be one of the first to join the future of sports fandom.
            </motion.p>
          </div>

          {/* Features */}
          <div className="p-8 pt-6">
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  className="p-4 rounded-xl bg-white/5 border border-white/10"
                >
                  <feature.icon className="text-primary mb-2" size={24} />
                  <h3 className="text-white font-semibold text-sm mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-text-muted text-xs">{feature.desc}</p>
                </motion.div>
              ))}
            </div>

            {/* Early Access Bonus */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="mt-6 p-4 rounded-xl bg-gradient-to-r from-primary/20 to-primary/10 border border-primary/30 text-center"
            >
              <div className="flex items-center justify-center gap-2 text-primary font-bold mb-1">
                <Sparkles size={18} />
                Founding Member Bonus
              </div>
              <p className="text-white text-sm">
                Sign up now and receive{" "}
                <span className="text-primary font-black text-lg">
                  {gtBonus.toLocaleString()} GT
                </span>
              </p>
              {hasTag && (
                <p className="text-text-muted text-xs mt-1">
                  + Exclusive{" "}
                  <span className="text-primary font-semibold">OG</span> profile
                  tag
                </p>
              )}
            </motion.div>

            {/* CTA */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              onClick={onClose}
              className="w-full mt-6 py-4 bg-primary text-black font-bold rounded-full shadow-neon hover:shadow-neon-lg transition-all duration-300"
            >
              Claim Your Spot
            </motion.button>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
              className="text-center text-text-muted text-xs mt-4"
            >
              Limited early access spots available
            </motion.p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    user,
    isAdmin,
    loginWithGoogle,
    loginWithEmail,
    signupWithEmail,
    sendPasswordReset,
    error,
    clearError,
    needsUsername,
    pendingSocialUser,
    completeSocialSignup,
    activeSignupOffer,
  } = useAuth();

  // Get dynamic offer data with fallback
  const offerGTBonus = activeSignupOffer?.gtBonus || 1000;
  const offerHasTag = !!activeSignupOffer?.tagId;

  // Redirect if user is already logged in (and doesn't need username setup)
  useEffect(() => {
    if (user && !needsUsername) {
      // Admins go to admin dashboard, everyone else goes to OG Hub
      navigate(isAdmin ? "/admin" : "/og-hub");
    }
  }, [user, needsUsername, isAdmin, navigate]);

  const [mode, setMode] = useState<AuthMode>("login");
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<"google" | "apple" | null>(
    null
  );
  const [showIntroModal, setShowIntroModal] = useState(false);

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [username, setUsername] = useState("");

  // Profile photo state for email signup
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validation
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState("");

  // Show intro modal on first visit
  useEffect(() => {
    const hasSeenIntro = localStorage.getItem("pg_seen_intro");
    if (!hasSeenIntro) {
      setShowIntroModal(true);
      localStorage.setItem("pg_seen_intro", "true");
    }
  }, []);

  // Handle username and photo completion for social signups
  const handleUsernameComplete = async (
    chosenUsername: string,
    photoFile?: File | null
  ) => {
    await completeSocialSignup(chosenUsername, photoFile);
    // New users always go to OG Hub (they can't be admin yet)
    navigate("/og-hub");
  };

  // Get suggested username from social profile
  const suggestedUsername =
    pendingSocialUser?.displayName
      ?.replace(/\s+/g, "")
      .replace(/[^a-zA-Z0-9_]/g, "")
      .slice(0, 20) || "";

  // Get suggested photo from social profile
  const suggestedPhotoURL = pendingSocialUser?.photoURL || null;

  // Handle photo selection for email signup
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setFormErrors({ ...formErrors, photo: "Please select an image file" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setFormErrors({ ...formErrors, photo: "Image must be less than 5MB" });
      return;
    }

    // Clear photo error if any
    const { photo, ...restErrors } = formErrors;
    setFormErrors(restErrors);
    setPhotoFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Please enter a valid email";
    }

    if (mode !== "forgot") {
      if (!password) {
        errors.password = "Password is required";
      } else if (password.length < 6) {
        errors.password = "Password must be at least 6 characters";
      }
    }

    if (mode === "signup") {
      if (!username.trim()) {
        errors.username = "Username is required";
      } else if (username.length < 3) {
        errors.username = "Username must be at least 3 characters";
      } else if (!/^[a-zA-Z0-9_]+$/.test(username)) {
        errors.username =
          "Username can only contain letters, numbers, and underscores";
      }

      if (password !== confirmPassword) {
        errors.confirmPassword = "Passwords do not match";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSuccessMessage("");

    if (!validateForm()) return;

    setLoading(true);

    try {
      if (mode === "login") {
        await loginWithEmail(email, password);
        // Redirect handled by useEffect watching user state
      } else if (mode === "signup") {
        await signupWithEmail(email, password, username, photoFile);
        // New signups always go to OG Hub
        navigate("/og-hub");
      } else if (mode === "forgot") {
        await sendPasswordReset(email);
        setSuccessMessage("Password reset email sent! Check your inbox.");
      }
    } catch (err) {
      // Error is handled by AuthContext
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    clearError();
    setSocialLoading("google");
    try {
      await loginWithGoogle();
      // Don't navigate yet - might need username setup
      // Navigation will happen after username is set or if user already exists
    } catch (err) {
      // Error handled by context
    } finally {
      setSocialLoading(null);
    }
  };

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    clearError();
    setFormErrors({});
    setSuccessMessage("");
  };

  return (
    <div className="min-h-screen bg-black flex">
      {/* Intro Modal */}
      <IntroModal
        isOpen={showIntroModal}
        onClose={() => setShowIntroModal(false)}
        gtBonus={offerGTBonus}
        hasTag={offerHasTag}
      />

      {/* Username Setup Modal for Social Signups */}
      <UsernameSetupModal
        isOpen={needsUsername}
        onSubmit={handleUsernameComplete}
        suggestedUsername={suggestedUsername}
        suggestedPhotoURL={suggestedPhotoURL}
      />

      {/* Left Side - Branding (Dark Theme) */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-black via-[#0A0A0A] to-black p-12 flex-col justify-between relative overflow-hidden"
      >
        {/* Background Glow Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-primary/10 rounded-full blur-[100px]" />
        </div>

        {/* Content */}
        <div className="relative z-10">
          <Link to="/" className="inline-flex items-center gap-2">
            <Logo size="md" variant="icon-only" forceTheme="dark" />{" "}
            <span className="text-2xl font-bold text-primary">PARLAY</span>
            <span className="text-2xl font-bold text-white">GROUND</span>
          </Link>
        </div>

        <div className="relative z-10 space-y-6">
          {/* Early Access Badge */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 border border-primary/30 rounded-full"
          >
            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            <span className="text-primary text-sm font-semibold uppercase tracking-wider">
              Early Access Open
            </span>
          </motion.div>

          <h1 className="text-5xl font-bold leading-tight">
            <span className="text-white">Get In</span>
            <br />
            <span className="text-primary">While It's Early.</span>
          </h1>
          <p className="text-xl text-text-body max-w-md">
            Be a founding member of the future of sports fandom. Early
            supporters get exclusive perks forever.
          </p>

          {/* Founding Member Benefits */}
          <div className="space-y-4 mt-8">
            {[
              `${offerGTBonus.toLocaleString()} GT founding member bonus`,
              ...(offerHasTag ? ["Exclusive OG profile tag forever"] : []),
              "Early access to all new features",
              "Shape the future of ParlayGround",
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.1 }}
                className="flex items-center gap-3 text-text-body"
              >
                <Check className="text-primary flex-shrink-0" size={18} />
                {feature}
              </motion.div>
            ))}
          </div>

          {/* Urgency Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-8 p-4 rounded-xl bg-white/5 border border-white/10"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <Users className="text-primary" size={24} />
              </div>
              <div>
                <div className="text-white font-semibold">Limited Spots</div>
                <div className="text-text-muted text-sm">
                  Founding member perks won't last forever
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="relative z-10 text-text-muted text-sm">
          © {new Date().getFullYear()} ParlayGround. All rights reserved.
        </div>
      </motion.div>

      {/* Right Side - Auth Form (Dark Theme) */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#0A0A0A]"
      >
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center gap-3">
            <Link to="/" className="inline-flex items-center gap-2">
              <Logo size="md" variant="icon-only" />
              <span className="text-2xl font-bold text-primary">PARLAY</span>
              <span className="text-2xl font-bold text-white">GROUND</span>
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 border border-primary/30 rounded-full">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <span className="text-primary text-xs font-semibold uppercase tracking-wider">
                Early Access
              </span>
            </div>
          </div>

          {/* Header */}
          <div className="text-center space-y-2">
            <h2 className="text-3xl font-bold text-white">
              {mode === "login" && "Welcome Back"}
              {mode === "signup" && "Get In Early"}
              {mode === "forgot" && "Reset Password"}
            </h2>
            <p className="text-text-muted">
              {mode === "login" && "Sign in to access your wallet"}
              {mode === "signup" &&
                `Claim your ${offerGTBonus.toLocaleString()} GT founding member bonus`}
              {mode === "forgot" && "We'll send you a reset link"}
            </p>
          </div>

          {/* Social Login Buttons */}
          {mode !== "forgot" && (
            <div className="space-y-3">
              <button
                onClick={handleGoogleLogin}
                disabled={socialLoading !== null}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white/10 hover:bg-white/15 border border-white/10 rounded-xl text-white font-medium transition-colors disabled:opacity-50"
              >
                {socialLoading === "google" ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Chrome size={20} />
                )}
                Continue with Google
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#0A0A0A] text-text-muted">
                    or continue with email
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-4 bg-error/10 border border-error/20 rounded-xl flex items-start gap-3"
              >
                <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
                <p className="text-error text-sm">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Success Message */}
          <AnimatePresence>
            {successMessage && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-4 bg-primary/10 border border-primary/20 rounded-xl"
              >
                <p className="text-primary text-sm">{successMessage}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <AnimatePresence mode="wait">
              {mode === "signup" && (
                <motion.div
                  key="signup-fields"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-5"
                >
                  {/* Profile Photo Upload */}
                  <div className="flex flex-col items-center">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handlePhotoSelect}
                      accept="image/*"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="relative group"
                    >
                      <img
                        src={photoPreview || DEFAULT_AVATAR}
                        alt="Profile"
                        className="w-20 h-20 rounded-full object-cover ring-2 ring-white/20 group-hover:ring-primary/50 transition-all"
                      />
                      <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Camera size={24} className="text-white" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-primary rounded-full flex items-center justify-center">
                        <Upload size={14} className="text-black" />
                      </div>
                    </button>
                    <p className="text-white/40 text-xs mt-2">
                      {photoFile
                        ? "Tap to change"
                        : "Add profile photo (optional)"}
                    </p>
                    {formErrors.photo && (
                      <p className="text-error text-xs mt-1">
                        {formErrors.photo}
                      </p>
                    )}
                  </div>

                  {/* Username Field */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Username
                    </label>
                    <div className="relative">
                      <User
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted"
                        size={18}
                      />
                      <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Choose a unique username"
                        className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                        autoComplete="username"
                      />
                    </div>
                    {formErrors.username && (
                      <p className="text-error text-sm mt-1">
                        {formErrors.username}
                      </p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Email
              </label>
              <div className="relative">
                <Mail
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted"
                  size={18}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                  autoComplete="email"
                />
              </div>
              {formErrors.email && (
                <p className="text-error text-sm mt-1">{formErrors.email}</p>
              )}
            </div>

            {mode !== "forgot" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted"
                      size={18}
                    />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                      autoComplete={
                        mode === "login" ? "current-password" : "new-password"
                      }
                    />
                  </div>
                  {formErrors.password && (
                    <p className="text-error text-sm mt-1">
                      {formErrors.password}
                    </p>
                  )}
                </div>

                <AnimatePresence mode="wait">
                  {mode === "signup" && (
                    <motion.div
                      key="confirm"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <label className="block text-sm font-medium text-white mb-2">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <Lock
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted"
                          size={18}
                        />
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="••••••••"
                          className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                          autoComplete="new-password"
                        />
                      </div>
                      {formErrors.confirmPassword && (
                        <p className="text-error text-sm mt-1">
                          {formErrors.confirmPassword}
                        </p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}

            {mode === "login" && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => switchMode("forgot")}
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-4 bg-primary text-black font-bold rounded-full shadow-neon hover:shadow-neon-lg transition-all duration-300 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                <>
                  {mode === "login" && "Sign In"}
                  {mode === "signup" && "Claim My Spot"}
                  {mode === "forgot" && "Send Reset Link"}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Footer Links */}
          <div className="text-center space-y-4">
            {mode === "login" && (
              <p className="text-text-muted">
                Don't have an account?{" "}
                <button
                  onClick={() => switchMode("signup")}
                  className="text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  Get early access
                </button>
              </p>
            )}

            {mode === "signup" && (
              <p className="text-text-muted">
                Already a member?{" "}
                <button
                  onClick={() => switchMode("login")}
                  className="text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  Sign in
                </button>
              </p>
            )}

            {mode === "forgot" && (
              <button
                onClick={() => switchMode("login")}
                className="text-primary hover:text-primary/80 font-medium transition-colors"
              >
                Back to sign in
              </button>
            )}

            <Link
              to="/"
              className="block text-text-muted text-sm hover:text-white transition-colors"
            >
              ← Back to homepage
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
